import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import PageTitle from "../../../layouts/PageTitle";
import {
	fetchStripePublicKey,
	formatApiError,
	getAgentDetail,
	payViaCredit,
	sendBookingEmail,
	stripePayment,
} from "../../../../services/TransferService";

function CreditCardForm({ bookingId, amount, currency, onSuccess }) {
	const stripe = useStripe();
	const elements = useElements();
	const [cardHolderName, setCardHolderName] = useState("");
	const [agreed, setAgreed] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(e) {
		e.preventDefault();
		if (!stripe || !elements) return;
		setSubmitting(true);
		const cardElement = elements.getElement(CardElement);
		const { error, token } = await stripe.createToken(cardElement);

		if (error) {
			formatApiError(null, error.message);
			setSubmitting(false);
			return;
		}

		try {
			const data = await stripePayment({
				payment_token: token.id,
				amount,
				booking_id: bookingId,
				payment_method: "stripe",
				currency,
				card_holder_name: cardHolderName,
			});
			onSuccess("card", data?.success || data?.message || "");
		} catch (err) {
			if (err.response) {
				formatApiError(err.response.data, "Payment failed. Please try again.");
			} else {
				formatApiError(null, "Unable to reach the payment server. Please try again.");
			}
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<form onSubmit={handleSubmit} className="border rounded p-3 mt-3">
			<div className="mb-3">
				<label className="form-label">Card Holder Name</label>
				<input
					type="text"
					className="form-control"
					value={cardHolderName}
					onChange={(e) => setCardHolderName(e.target.value)}
					placeholder="Enter Your Name"
				/>
			</div>
			<div className="mb-3">
				<label className="form-label">Card Number</label>
				<div className="form-control" style={{ paddingTop: "0.65rem" }}>
					<CardElement options={{ hidePostalCode: true }} />
				</div>
			</div>
			<div className="form-check mb-3">
				<input
					id="terms"
					type="checkbox"
					className="form-check-input"
					checked={agreed}
					onChange={() => setAgreed((prev) => !prev)}
				/>
				<label htmlFor="terms" className="form-check-label">
					By continuing, you agree to the Terms and Conditions.
				</label>
			</div>
			<div className="text-end">
				<button type="submit" className="btn btn-primary rounded-pill px-4" disabled={!agreed || submitting || !cardHolderName}>
					{submitting ? "Processing..." : "Process Payment"} <i className="fa fa-paper-plane ms-2" />
				</button>
			</div>
		</form>
	);
}

function OnAccountPanel({ bookingId, amount, currency, onSuccess }) {
	const agent = useSelector((state) => state.auth.auth) || {};
	const [loading, setLoading] = useState(true);
	const [detail, setDetail] = useState(null);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		getAgentDetail(agent.id)
			.then(setDetail)
			.finally(() => setLoading(false));
	}, [agent.id]);

	const hasCredit = detail?.credit === "Yes";
	const availableCredit = Number(detail?.credit_limit) || 0;
	const sufficient = hasCredit && availableCredit >= amount;

	async function handlePay() {
		setSubmitting(true);
		try {
			const data = await payViaCredit({
				payment_token: "",
				amount,
				booking_id: bookingId,
				payment_method: "credit",
				currency,
				agent_id: agent.id,
			});
			onSuccess("account", data?.success || data?.message || "");
		} catch (err) {
			if (err.response) {
				formatApiError(err.response.data, "Unable to process payment on account. Please try again.");
			} else {
				formatApiError(null, "Unable to reach the server. Please try again.");
			}
		} finally {
			setSubmitting(false);
		}
	}

	if (loading) {
		return <div className="border rounded p-3 mt-3 text-muted">Checking account balance...</div>;
	}

	return (
		<div className="border rounded p-3 mt-3">
			<div className="d-flex justify-content-between mb-2">
				<span className="fw-500">Available Credit</span>
				<span>{detail?.currency || currency} {availableCredit.toFixed(2)}</span>
			</div>
			<div className="d-flex justify-content-between mb-3">
				<span className="fw-500">Amount Due</span>
				<span>{currency} {Number(amount).toFixed(2)}</span>
			</div>
			{!sufficient && (
				<div className="alert alert-warning">
					Insufficient credit/balance available on this account to complete this booking.
				</div>
			)}
			<div className="text-end">
				<button type="button" className="btn btn-primary rounded-pill px-4" disabled={!sufficient || submitting} onClick={handlePay}>
					{submitting ? "Processing..." : "Pay with On Account Balance"} <i className="fa fa-paper-plane ms-2" />
				</button>
			</div>
		</div>
	);
}

const TransferPayment = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const agent = useSelector((state) => state.auth.auth) || {};
	const bookingId = location.state?.bookingId;
	const totalCharge = location.state?.totalCharge;
	const transferData = location.state?.transferData;
	const searchData = location.state?.searchData;

	const [method, setMethod] = useState("card");
	const [stripePromise, setStripePromise] = useState(null);

	useEffect(() => {
		fetchStripePublicKey()
			.then((key) => key && setStripePromise(loadStripe(key)))
			.catch(() => setStripePromise(null));
	}, []);

	function handleSuccess(paymentMethod, paymentMessage) {
		// Fire-and-forget: payment already went through, so a failed email shouldn't block the flow.
		sendBookingEmail(bookingId).catch((err) => console.error("Failed to send booking email", err));
		navigate("/transfer-confirmation", {
			state: { bookingId, totalCharge, transferData, searchData, paymentMethod, paymentMessage },
		});
	}

	if (!bookingId) {
		return (
			<div className="card">
				<div className="card-body text-center py-5">
					<p className="mb-3">No booking in progress. Please start a new search.</p>
					<button type="button" className="btn btn-primary" onClick={() => navigate("/dashboard")}>
						Back to Search
					</button>
				</div>
			</div>
		);
	}

	const currency = agent.currency || "AUD";

	return (
		<div className="transfer-flow">
			<PageTitle activeMenu="Payment" motherMenu="Dashboard" />
			<div className="row align-items-start">
				<div className="col-lg-8">
					<div className="card">
						<div className="card-body">
							<h4 className="mb-3">Choose Payment Method</h4>
							<div className="row g-3">
								<div className="col-sm-6">
									<button
										type="button"
										className={`btn w-100 py-3 ${method === "card" ? "btn-outline-primary border-2" : "btn-outline-secondary"}`}
										onClick={() => setMethod("card")}
									>
										<i className="fa fa-credit-card fs-24 d-block mb-2" />
										Payment with Credit Card
									</button>
								</div>
								<div className="col-sm-6">
									<button
										type="button"
										className={`btn w-100 py-3 ${method === "account" ? "btn-outline-primary border-2" : "btn-outline-secondary"}`}
										onClick={() => setMethod("account")}
									>
										<i className="fa fa-wallet fs-24 d-block mb-2" />
										On Account
									</button>
								</div>
							</div>

							{method === "card" &&
								(stripePromise ? (
									<Elements stripe={stripePromise}>
										<CreditCardForm bookingId={bookingId} amount={totalCharge} currency={currency} onSuccess={handleSuccess} />
									</Elements>
								) : (
									<div className="text-muted mt-3">Loading payment form...</div>
								))}

							{method === "account" && (
								<OnAccountPanel bookingId={bookingId} amount={totalCharge} currency={currency} onSuccess={handleSuccess} />
							)}
						</div>
					</div>
				</div>
				<div className="col-lg-4">
					<div className="card">
						<div className="card-body">
							<h5 className="mb-3">Amount Due</h5>
							<h3 className="text-primary">{currency} {Number(totalCharge || 0).toFixed(2)}</h3>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default TransferPayment;
